import { useMemo } from "react";
import { Button, Input, Select, Switch, Typography } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import type { SubjectEntity } from "../../Processing/types";
import type { RadarSubjectEntry } from "./radarState";
import { ColorSwatchPicker } from "./RadarColorSwatchPicker";

const { Text } = Typography;
const { OptGroup, Option } = Select;

export type SubjectEntityType = "team" | "player" | "position" | "drill" | "group";

interface SubjectRowProps {
  entry: RadarSubjectEntry;
  index: number;
  fallbackColor: string;
  getSubjectEntities: (t: string) => SubjectEntity[];
  canRemove: boolean;
  allowedTypes: SubjectEntityType[];
  showSeriesControls?: boolean;
  onChange: (patch: Partial<Omit<RadarSubjectEntry, "queryIds">>) => void;
  onRemove: () => void;
}

// allowedTypes controls which OptGroups are rendered.
// showSeriesControls=true adds color swatch, fill toggle, and rename input.
export function SubjectRow({
  entry,
  index,
  fallbackColor,
  getSubjectEntities,
  canRemove,
  allowedTypes,
  showSeriesControls,
  onChange,
  onRemove,
}: SubjectRowProps) {
  const players   = useMemo(() => allowedTypes.includes("player")   ? getSubjectEntities("player")   : [], [getSubjectEntities, allowedTypes]);
  const positions = useMemo(() => allowedTypes.includes("position") ? getSubjectEntities("position") : [], [getSubjectEntities, allowedTypes]);
  const groups    = useMemo(() => allowedTypes.includes("group")    ? getSubjectEntities("group")    : [], [getSubjectEntities, allowedTypes]);
  const drills    = useMemo(() => allowedTypes.includes("drill")    ? getSubjectEntities("drill")    : [], [getSubjectEntities, allowedTypes]);

  const indent = 24;

  const handleSubjectChange = (v: string) => {
    const displayName = resolveSubjectName(v, { players, positions, groups, drills });
    onChange({ subjectFilter: v, displayName });
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        padding: "8px 10px",
        border: "1px solid #e0e5eb",
        borderRadius: 8,
        background: "#fafbfc",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <Text style={{ fontSize: 11, color: "#888", minWidth: 18 }}>{index + 1}</Text>

        <Select
          style={{ flex: 1 }}
          size="small"
          value={entry.subjectFilter || undefined}
          placeholder="Choose subject"
          onChange={handleSubjectChange}
          showSearch
          optionFilterProp="label"
        >
          {allowedTypes.includes("team") && (
            <OptGroup label="Team">
              <Option value="full-team" label="Full Team">Full Team</Option>
            </OptGroup>
          )}
          {players.length > 0 && (
            <OptGroup label="Players">
              {players.map((p) => (
                <Option key={`player-${p.id}`} value={`player-${p.id}`} label={p.name}>
                  {p.name}
                  {p.detail && (
                    <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>{p.detail}</Text>
                  )}
                </Option>
              ))}
            </OptGroup>
          )}
          {positions.length > 0 && (
            <OptGroup label="Positions">
              {positions.map((pos) => (
                <Option key={`pos-${pos.id}`} value={`pos-${pos.id}`} label={pos.name}>
                  {pos.name}
                </Option>
              ))}
            </OptGroup>
          )}
          {groups.length > 0 && (
            <OptGroup label="Groups">
              {groups.map((g) => (
                <Option key={`group-${g.id}`} value={`group-${g.id}`} label={g.name}>
                  {g.name}
                </Option>
              ))}
            </OptGroup>
          )}
          {drills.length > 0 && (
            <OptGroup label="Drills">
              {drills.map((d) => (
                <Option key={`drill-${d.id}`} value={`drill-${d.id}`} label={d.name}>
                  {d.name}
                </Option>
              ))}
            </OptGroup>
          )}
        </Select>

        {canRemove && (
          <Button
            type="text"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={onRemove}
          />
        )}
      </div>

      {showSeriesControls && (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 8, paddingLeft: indent }}>
            <Text style={{ fontSize: 11, color: "#888", minWidth: 88 }}>Fill</Text>
            <Switch
              size="small"
              checked={entry.fillArea ?? false}
              onChange={(v) => onChange({ fillArea: v })}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, paddingLeft: indent }}>
            <Text style={{ fontSize: 11, color: "#888", minWidth: 88 }}>Override color</Text>
            <Switch
              size="small"
              checked={!!entry.colorOverride}
              onChange={(on) => onChange({ colorOverride: on ? fallbackColor : undefined })}
            />
            {entry.colorOverride && (
              <ColorSwatchPicker
                color={entry.colorOverride}
                onChange={(hex) => onChange({ colorOverride: hex })}
              />
            )}
          </div>
        </>
      )}

      {entry.subjectFilter && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, paddingLeft: indent }}>
          <Text style={{ fontSize: 11, color: "#888", minWidth: showSeriesControls ? 88 : 36 }}>Override label</Text>
          <Switch
            size="small"
            checked={entry.displayNameOverride !== undefined}
            onChange={(on) => onChange({ displayNameOverride: on ? entry.displayName : undefined })}
          />
          {entry.displayNameOverride !== undefined && (
            <Input
              size="small"
              placeholder={entry.displayName || "Label"}
              value={entry.displayNameOverride}
              onChange={(e) => onChange({ displayNameOverride: e.target.value })}
              style={{ flex: 1, fontSize: 11 }}
            />
          )}
        </div>
      )}
    </div>
  );
}

function resolveSubjectName(
  subjectFilter: string,
  entities: { players: SubjectEntity[]; positions: SubjectEntity[]; groups: SubjectEntity[]; drills: SubjectEntity[] },
): string {
  if (subjectFilter === "full-team") return "Full Team";
  return (
    entities.players.find((p)   => `player-${p.id}`  === subjectFilter)?.name ??
    entities.positions.find((p) => `pos-${p.id}`     === subjectFilter)?.name ??
    entities.groups.find((g)    => `group-${g.id}`   === subjectFilter)?.name ??
    entities.drills.find((d)    => `drill-${d.id}`   === subjectFilter)?.name ??
    subjectFilter
  );
}
